import { useCallback, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { Button } from '@antimatterhq/ui/components/button';
import { StreamTablesSchemaValues } from '@/zod/schemas/transforms/TransformSchema';
import { StreamTable } from '@/components/streamTable/StreamTable';
import { StreamTableSchemaValues } from '@/zod/schemas/StreamTableSchema';

export const TransformStreamForm = () => {
  const { values, setFieldValue } =
    useFormikContext<StreamTablesSchemaValues>();

  const createTable = useCallback(() => {
    const table: StreamTableSchemaValues = {
      name: '',
      streaming: true,
      watermark: {
        eventTimeColumn: '',
        delayThreshold: '',
        dropDuplicates: [],
      },
      alias: '',
      joinType: '',
      joinExpr: '',
      joinHidden: false,
      aliasRequired: false,
    };

    return table;
  }, []);

  useEffect(() => {
    // if we are in streaming mode, we need to create at least one streaming table
    if (values.tables.length === 0) {
      setFieldValue('tables', [createTable()]);
      return;
    }

    let dirty = false;
    if (!values.tables[0].joinHidden) {
      values.tables[0].joinHidden = true;
      dirty = true;
    }

    if (
      values.tables.length > 1 &&
      values.tables.some((table) => !table.aliasRequired)
    ) {
      // eslint-disable-next-line no-param-reassign
      values.tables.forEach((table) => (table.aliasRequired = true));
      dirty = true;
    }

    if (values.tables.length === 1 && values.tables[0].aliasRequired) {
      values.tables[0].aliasRequired = false;
      dirty = true;
    }

    if (dirty) {
      setFieldValue('tables', values.tables);
    }
  }, [values, setFieldValue, createTable]);

  return (
    <div>
      <Spacer size="base" />
      <Label required>Source tables:</Label>
      <FieldArray name="tables">
        {({ remove, push }) => (
          <>
            {values.tables.map((table, index) => (
              <div key={`streamingTables-${index}`}>
                {index > 0 && <Spacer size="lg" />}
                <StreamTable
                  index={index}
                  table={table}
                  remove={remove}
                  prefix="tables"
                  hideRemove={index === 0}
                  hideInputType={false}
                />
              </div>
            ))}

            <Spacer size="lg" />

            <Button variant="default" onClick={() => push(createTable())}>
              Add table
            </Button>
          </>
        )}
      </FieldArray>
      <Spacer size="lg" />
    </div>
  );
};
